// THE wiring between raw POI rows and the Browser pane's two trees.
//
// buildPOIHierarchy holds the pure builders; this hook holds the field-readers every consumer
// injected into them. It exists because the readers are NOT viewer-specific — they are thin
// wrappers over the canonical StoreyClassifier / POIFieldExtractor helpers over plain POI
// fields — so each viewer growing its own copy would be four subtly different trees for one
// dataset. AutodeskViewer, WebIfcViewer and the App-level panorama/2D panes all call this.
//
// Input is the raw `/assets/:id/pois` rows (aggregated, so `poi.locations` is present) plus the
// asset's custom rooms; a viewer with no custom rooms simply passes none.

import { useMemo } from 'react';
import {
  buildLocationsHierarchy,
  buildSystemsHierarchy,
  CustomRoomInput,
  HierarchyNode,
} from './buildPOIHierarchy';
import {
  buildRvtLevelAliases,
  getStoreyNameForPOI,
  isSpaceType,
} from '../../services/spatial/StoreyClassifier';
import { parseCustomData } from '../../utils/POIFieldExtractor';

/** Display name for a POI row. */
export const getPOIDisplayName = (poi: any): string => poi.title || poi.name || 'Unnamed';

/**
 * Room/space name for a POI row. The joined location wins when it really is a Space; otherwise
 * fall back to the modelling properties in custom_data (Revit's Location / Room Name + Room
 * Number, then the IFC/RVT equivalents). Moved verbatim out of AutodeskViewer, which was the
 * only viewer that had it.
 */
export const getPOISpaceName = (poi: any): string | null => {
  if (isSpaceType(poi.location_type)) {
    return poi.location_name || null;
  }

  const customData = parseCustomData(poi) || {};

  // rvtProperties['Location'] carries the combined "Room Name + Room Number" Revit shows.
  const rvtProps = customData?.rvt?.rvtProperties || {};
  if (rvtProps['Location']) return rvtProps['Location'];
  if (rvtProps['Room Name']) {
    const roomNumber = rvtProps['Room Number'] || '';
    return roomNumber ? `${rvtProps['Room Name']} ${roomNumber}` : rvtProps['Room Name'];
  }

  if (customData?.ifc?.properties?.Space) return customData.ifc.properties.Space;
  if (customData?.ifc?.properties?.Room) return customData.ifc.properties.Room;
  if (customData?.rvt?.room) return customData.rvt.room;
  if (customData?.rvt?.properties?.Room) return customData.rvt.properties.Room;
  return null;
};

export interface POIHierarchies {
  locationsHierarchy: HierarchyNode[];
  systemsHierarchy: HierarchyNode[];
}

const NO_CUSTOM_ROOMS: CustomRoomInput[] = [];

/**
 * Build both Browser-pane trees from POI rows (+ optional custom rooms). Memoized on the inputs,
 * so a consumer can call it directly in render.
 */
export function usePOIHierarchies(
  pois: any[],
  customRooms: CustomRoomInput[] = NO_CUSTOM_ROOMS,
): POIHierarchies {
  // Revit level *indices* only resolve to names by looking across the whole POI set.
  const rvtLevelAliases = useMemo(() => buildRvtLevelAliases(pois), [pois]);

  const locationsHierarchy = useMemo(
    () => buildLocationsHierarchy(pois, customRooms, {
      getPOIStorey: (poi: any) => getStoreyNameForPOI(poi, rvtLevelAliases) ?? 'Unassigned',
      getPOISpace: getPOISpaceName,
      getPOIName: getPOIDisplayName,
      isSpaceType,
    }),
    [pois, customRooms, rvtLevelAliases],
  );

  const systemsHierarchy = useMemo(
    () => buildSystemsHierarchy(pois, getPOIDisplayName),
    [pois],
  );

  return { locationsHierarchy, systemsHierarchy };
}
